/**
 * Robust "is this submitter the current user?" check.
 *
 * Different parts of the system stamp the submitter field in different ways
 * (Okta email, preferred_username, local-part-only, full name). Strict
 * equality on `userInfo.email` alone misses builds when the token claims
 * format doesn't match exactly — so we try every plausible identifier and
 * fall back to a local-part / contains check.
 */
export interface UserIdentity {
  email?: string;
  preferred_username?: string;
  nickname?: string;
  sub?: string;
  name?: string;
}

const norm = (v: string | undefined | null): string =>
  (v ?? '').toString().trim().toLowerCase();

// Strips spaces, dots, dashes, underscores, plus signs — folds names like
// "Mohamed Salman Faarsi", "mohamed-salman-faarsi", "mohamedsalman.faarsi"
// all to "mohamedsalmanfaarsi" so any of them match each other.
const flat = (v: string): string => norm(v).replace(/[\s._\-+]+/g, '');

export function matchesCurrentUser(
  submitter: string | undefined | null,
  user: UserIdentity | null | undefined,
): boolean {
  if (!user || !submitter) return false;
  const s = norm(submitter);
  if (!s) return false;

  const candidates: string[] = [];
  if (user.email) candidates.push(norm(user.email));
  if (user.preferred_username) candidates.push(norm(user.preferred_username));
  if (user.nickname) candidates.push(norm(user.nickname));
  if (user.sub) candidates.push(norm(user.sub));
  if (user.name) candidates.push(norm(user.name));

  // 1. Exact match on any candidate identifier.
  if (candidates.some(c => c && c === s)) return true;

  // 2. Local-part match after flattening (handles "user@x.com" submitter vs
  //    full-name claim "First Last", or "first.last" vs "First Last").
  const sFlat = flat(s.includes('@') ? s.split('@')[0] : s);
  for (const c of candidates) {
    if (!c) continue;
    const local = c.includes('@') ? c.split('@')[0] : c;
    const cFlat = flat(local);
    if (cFlat && sFlat && cFlat.length >= 6 && sFlat.length >= 6 && cFlat === sFlat) {
      return true;
    }
  }

  // 3. Containment fallback for tokens that include extra context
  //    (e.g. "Mohamed Salman Faarsi <user@x.com>" or "user [admin]").
  return candidates.some(c => c && c.length >= 4 && (s.includes(c) || c.includes(s)));
}
